import { useSetAtom } from "jotai";
import { useState } from "react";
import { Button, Col, Collapse, Row } from "reactstrap";
import ComplexHeaderGroup from "./ComplexHeaderGroup";
import { getBaseFields, mappedHeadersAtom } from "./ImportDataWidget";
import produce from "immer";
import ActionsPicker from "./ActionsPicker";
import ImportOptionsDropdown from "./ImportOptionsDropdown";
import Constants from "utils/Constants";
import FieldUtils from "../../../utils/FieldUtils";

const acceptableActions = ["record-create-update"];
const iconMap = Constants.MAPPING_ICONS;

export const ChevronTitle = ({ open, title, css = "", onClick }) => {
  return (
    <div
      className={`d-flex align-items-center cursor-pointer ${css}`}
      onClick={onClick ? onClick : null}
    >
      <i
        className={`mdi mdi-chevron-${open ? "down" : "right"} text-dark px-2`}
        style={{ fontSize: 20 }}
      />
      {title}
    </div>
  );
};

const ComplexMappedHeader = ({
  mappedHeader,
  field,
  availableActions,
  subitemFieldMap, // Provided when the field is a subitem
  doubleNestedParent, // For when a Subitem has a complex field.
  doubleNestedIndex, // For when a Subitem has a complex field.
  usesActions,
  fileHeaders,
  selectedHeaders,
}) => {
  const [open, setOpen] = useState(true);
  const setMappedHeaders = useSetAtom(mappedHeadersAtom);
  const useIdentifiers = mappedHeader.Type === "Identifier";
  const useAction = mappedHeader.Type === "Action";
  const isSingleSubitem = field?.FieldType === "Subitem";
  const isPartiallyComplex = FieldUtils.isPartiallyComplex(field?.FieldType);

  return (
    <div className="border rounded mb-2">
      <Row
        className="align-items-center"
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        <Col xs="6" sm="5">
          <ChevronTitle
            title={field?.Name}
            open={open}
            onClick={() => setOpen((prev) => !prev)}
            css="ml--3"
          />
        </Col>
        <Col xs="6" sm="7">
          <div
            className="d-flex align-items-center my-1"
            style={{ position: "relative" }}
          >
            {useAction && (
              <ActionsPicker
                availableActions={availableActions}
                availableActionTypes={acceptableActions}
                onChange={onActionsPickerChange}
                value={getActionsPickerValue()}
              />
            )}
            {useIdentifiers && (
              <div className="border small w-100 text-dark rounded-sm py-1 pl-2 bg-superlight">
                Using Data From File
              </div>
            )}
            <div className="ml-2">
              <ImportOptionsDropdown
                onModeSwitch={onModeSwitch}
                onDeleteColumn={handleDeleteField}
                isComplexMappedHeader
                usesActions={usesActions}
              />
            </div>
            <i
              className={iconMap[useAction ? "Action" : "Identifier"]}
              style={{ position: "absolute", left: -25, top: 1, fontSize: 22 }}
            />
          </div>
        </Col>
      </Row>
      <Collapse isOpen={open}>
        {useIdentifiers && (
          <>
            {mappedHeader.Fields?.map((item, i) => {
              return (
                <ComplexHeaderGroup
                  key={i}
                  headerItem={item}
                  index={i}
                  field={field}
                  availableActions={availableActions}
                  subitemFieldMap={subitemFieldMap}
                  doubleNestedParent={doubleNestedParent}
                  doubleNestedIndex={doubleNestedIndex}
                  fileHeaders={fileHeaders}
                  selectedHeaders={selectedHeaders}
                />
              );
            })}
            {!isSingleSubitem && !isPartiallyComplex && (
              <div className="d-flex justify-content-end">
                <Button
                  onClick={handleAddGroup}
                  outline
                  color="primary"
                  size="sm"
                  style={{ alignSelf: "self-end" }}
                >
                  Add Group
                </Button>
              </div>
            )}
          </>
        )}
        {useAction && (
          <div
            className="p-2 text-center border-top font-weight-500 text-dark"
            style={{
              background: mappedHeader?.ActionID
                ? "var(--dropdown-selected)"
                : "#feffba",
            }}
          >
            {mappedHeader?.ActionID
              ? "The result of the selected action will be used for this field."
              : "Select an action to complete this field."}
          </div>
        )}
      </Collapse>
    </div>
  );

  function onModeSwitch(mode) {
    if (mode === "Identifier") {
      updateMappedHeader(
        {
          Type: "Identifier",
          Fields: [
            {
              Fields: getBaseFields(field?.FieldType, field?.SubitemFields),
            },
          ],
        },
        ["ActionID"]
      );
    }

    if (mode === "Action") {
      updateMappedHeader({ Type: "Action", ActionID: "" }, ["Fields"]);
    }
  }

  function handleDeleteField() {
    setMappedHeaders(
      produce((newHeaders) => {
        if (doubleNestedParent) {
          delete newHeaders[doubleNestedParent].Fields[doubleNestedIndex]
            .Fields[field?.APIName];
        } else {
          delete newHeaders[field?.APIName];
        }
      })
    );
  }

  function handleAddGroup(e) {
    e.stopPropagation();
    setMappedHeaders(
      produce((newHeaders) => {
        if (doubleNestedParent) {
          newHeaders[doubleNestedParent].Fields[doubleNestedIndex].Fields[
            field?.APIName
          ].Fields = [
            ...newHeaders[doubleNestedParent].Fields[doubleNestedIndex].Fields[
              field?.APIName
            ].Fields,
            {
              Fields: getBaseFields(field?.FieldType),
            },
          ];
        } else {
          newHeaders[field?.APIName].Fields = [
            ...newHeaders[field?.APIName].Fields,
            {
              Fields: getBaseFields(field?.FieldType, field?.SubitemFields),
            },
          ];
        }
      })
    );
  }

  function updateMappedHeader(newValues, deleteKeys) {
    setMappedHeaders(
      produce((newHeaders) => {
        for (const key of Object.keys(newValues)) {
          if (doubleNestedParent) {
            newHeaders[doubleNestedParent].Fields[doubleNestedIndex].Fields[
              field?.APIName
            ][key] = newValues[key];
          } else {
            newHeaders[field?.APIName][key] = newValues[key];
          }
        }

        for (const key of deleteKeys) {
          if (doubleNestedParent) {
            delete newHeaders[doubleNestedParent].Fields[doubleNestedIndex]
              .Fields[field?.APIName][key];
          } else {
            delete newHeaders[field?.APIName][key];
          }
        }
      })
    );
  }

  function onActionsPickerChange(newValue) {
    updateMappedHeader({ Type: "Action", ActionID: newValue.ActionID }, [
      "Fields",
    ]);
  }

  function getActionsPickerValue() {
    if (mappedHeader?.Type === "Identifier") {
      return "Identifier";
    }
    if (mappedHeader?.Type === "Action") {
      return mappedHeader?.ActionID;
    }

    return "";
  }
};

export default ComplexMappedHeader;
